import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { checkUserTypeOptimized, loadProfileOptimized, clearQueryCache } from '../utils/queryOptimizer';

// Criar o contexto
const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // Refs para evitar race conditions
  const isInitialized = useRef(false);
  const currentUserId = useRef(null);

  // Carregar usuário logado e configurar listener de autenticação
  useEffect(() => {
    // Evitar inicialização múltipla
    if (isInitialized.current) {
      return;
    }
    isInitialized.current = true;

    // Verificar sessão atual
    const getSession = async () => {
      try {
        console.log('🔄 Verificando sessão inicial...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ Erro ao obter sessão:', error);
          setAuthError('Erro ao verificar sessão');
          setLoading(false);
          return;
        }
        
        if (session?.user) {
          console.log('✅ Sessão encontrada, carregando perfil...');
          currentUserId.current = session.user.id;
          
          try {
            // Primeiro verificar o tipo de usuário
            const userType = await checkUserTypeOptimized(session.user.id);
            console.log('✅ Tipo de usuário detectado:', userType);
            
            // Depois carregar o perfil otimizado
            const profile = await loadProfileOptimized(session.user, userType);
            
            // Criar o usuário com perfil carregado
            const userWithProfile = {
              ...session.user,
              userType: userType,
              profile: profile || {
                id: session.user.id,
                email: session.user.email,
                name: session.user.user_metadata?.name || session.user.email.split('@')[0],
                phone: '',
                department: userType === 'student' ? 'Aluno' : 'Administrativo',
                role: userType === 'student' ? 'student' : 'admin',
                avatar_url: '',
                created_at: session.user.created_at,
                updated_at: new Date().toISOString(),
                is_active: true,
                userType: userType
              }
            };
            
            console.log('✅ Usuário com perfil carregado na sessão:', {
              id: userWithProfile.id,
              email: userWithProfile.email,
              userType: userWithProfile.userType
            });
            
            setUser(userWithProfile);
            setLoading(false);
            setAuthError(null);
            
          } catch (error) {
            console.error('❌ Erro ao carregar perfil na sessão:', error);
            
            // Em caso de erro, criar usuário com perfil padrão
            const userWithDefaultProfile = {
              ...session.user,
              userType: 'admin',
              profile: {
                id: session.user.id,
                email: session.user.email,
                name: session.user.user_metadata?.name || session.user.email.split('@')[0],
                phone: '',
                department: 'Administrativo',
                role: 'admin',
                avatar_url: '',
                created_at: session.user.created_at,
                updated_at: new Date().toISOString(),
                is_active: true,
                userType: 'admin'
              }
            };
            
            console.log('⚠️ Usando perfil padrão na sessão devido ao erro');
            setUser(userWithDefaultProfile);
            setLoading(false);
            setAuthError('Erro ao carregar perfil, usando configuração padrão');
          }
        } else {
          console.log('ℹ️ Nenhuma sessão encontrada');
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Erro geral ao verificar sessão:', error);
        setAuthError('Erro interno ao verificar sessão');
        setLoading(false);
      }
    };

    getSession();

    // Listener para mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state change:', { event, user: session?.user?.email });
      
      // Evitar processamento duplo
      if (isProcessingAuth) {
        console.log('⏳ Já processando autenticação, pulando...');
        return;
      }
      
      // Evitar processar o mesmo usuário múltiplas vezes
      if (session?.user?.id === currentUserId.current && event === 'SIGNED_IN') {
        console.log('⏳ Usuário já processado, pulando...');
        return;
      }
      
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('✅ Usuário logado, carregando perfil...');
        currentUserId.current = session.user.id;
        setIsProcessingAuth(true);
        setLoading(true);
        setAuthError(null);
        
        try {
          // Primeiro verificar o tipo de usuário
          const userType = await checkUserTypeOptimized(session.user.id);
          console.log('✅ Tipo de usuário detectado:', userType);
          
          // Depois carregar o perfil otimizado
          const profile = await loadProfileOptimized(session.user, userType);
          
          // Criar o usuário com perfil carregado
          const userWithProfile = {
            ...session.user,
            userType: userType,
            profile: profile || {
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.name || session.user.email.split('@')[0],
              phone: '',
              department: userType === 'student' ? 'Aluno' : 'Administrativo',
              role: userType === 'student' ? 'student' : 'admin',
              avatar_url: '',
              created_at: session.user.created_at,
              updated_at: new Date().toISOString(),
              is_active: true,
              userType: userType
            }
          };
          
          console.log('✅ Usuário com perfil criado:', {
            id: userWithProfile.id,
            email: userWithProfile.email,
            userType: userWithProfile.userType,
            role: userWithProfile.profile.role
          });
          
          setUser(userWithProfile);
          setLoading(false);
          setAuthError(null);
          
        } catch (error) {
          console.error('❌ Erro ao carregar perfil após login:', error);
          
          // Em caso de erro, criar usuário com perfil padrão
          const userWithDefaultProfile = {
            ...session.user,
            userType: 'admin',
            profile: {
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.name || session.user.email.split('@')[0],
              phone: '',
              department: 'Administrativo',
              role: 'admin',
              avatar_url: '',
              created_at: session.user.created_at,
              updated_at: new Date().toISOString(),
              is_active: true,
              userType: 'admin'
            }
          };
          
          console.log('⚠️ Usando perfil padrão devido ao erro:', userWithDefaultProfile.email);
          setUser(userWithDefaultProfile);
          setLoading(false);
          setAuthError('Erro ao carregar perfil, usando configuração padrão');
        } finally {
          setIsProcessingAuth(false);
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('🚪 Usuário deslogado');
        currentUserId.current = null;
        setUser(null);
        setLoading(false);
        setIsProcessingAuth(false);
        setAuthError(null);
      }
    });

    return () => {
      subscription.unsubscribe();
      isInitialized.current = false;
    };
  }, []);

  // Funções antigas removidas - agora usando queryOptimizer otimizado

    // Função loadUserProfile antiga removida - agora usando queryOptimizer otimizado

  // Função de registro
  const register = async (userData) => {
    try {
      // Registrar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            name: userData.name
          }
        }
      });

      if (authError) {
        throw new Error(authError.message);
      }

      if (!authData.user) {
        throw new Error('Erro ao criar usuário');
      }

      // Tentar criar perfil na tabela profiles (opcional)
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: authData.user.id,
            email: userData.email,
            name: userData.name,
            phone: userData.phone || '',
            department: userData.department || '',
            role: 'admin', // Sempre admin conforme solicitado
            avatar_url: '',
            is_active: true
          });

        if (profileError) {
          console.warn('Não foi possível criar perfil na tabela profiles:', profileError);
        } else {
          console.log('Perfil criado com sucesso na tabela profiles');
        }
      } catch (profileError) {
        console.warn('Erro ao tentar criar perfil na tabela profiles:', profileError);
      }

      return { success: true, user: authData.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Função de login
  const login = async (email, password) => {
    try {
      console.log('Attempting login for:', email);
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      console.log('Login response:', { authData, authError });

      if (authError) {
        console.error('Login error:', authError);
        throw new Error(authError.message);
      }

      if (!authData.user) {
        console.error('No user in auth data');
        throw new Error('Erro ao fazer login');
      }

      console.log('Login successful for user:', authData.user.email);
      return { success: true, user: authData.user };
    } catch (error) {
      console.error('Login catch error:', error);
      return { success: false, error: error.message };
    }
  };

  // Função de logout
  const logout = async () => {
    try {
      console.log('🚪 LOGOUT: Iniciando processo de logout...');
      console.log('👤 LOGOUT: Usuário atual:', user?.email);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('❌ LOGOUT ERROR:', error);
        throw error;
      }
      
      console.log('✅ LOGOUT: Supabase signOut concluído');
      setUser(null);
      console.log('✅ LOGOUT: Estado do usuário limpo');
      console.log('🔄 LOGOUT: Processo concluído com sucesso');
    } catch (error) {
      console.error('❌ LOGOUT FAILED:', error);
      // Mesmo com erro, limpar o estado local
      setUser(null);
    }
  };

  // Função para atualizar perfil
  const updateProfile = async (profileData) => {
    try {
      if (!user?.id) {
        throw new Error('Usuário não encontrado');
      }

      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          ...profileData,
          updated_at: new Date().toISOString()
        });

      if (error) {
        throw new Error(error.message);
      }

      // Recarregar perfil do usuário
      try {
        const userType = await checkUserTypeOptimized(user.id);
        const updatedProfile = await loadProfileOptimized(user, userType);
        
        // Atualizar o usuário com o novo perfil
        const updatedUser = {
          ...user,
          userType: userType,
          profile: updatedProfile || user.profile
        };
        
        setUser(updatedUser);
        console.log('✅ Perfil atualizado com sucesso');
        
        // Limpar cache relacionado ao usuário
        clearQueryCache(`profile_${user.id}`);
        clearQueryCache(`user_type_${user.id}`);
        
      } catch (profileError) {
        console.warn('⚠️ Erro ao recarregar perfil após atualização:', profileError.message);
        // Mesmo com erro, a atualização foi bem-sucedida
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    authError,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user
  };

  // Verificar se o valor está definido antes de renderizar
  if (!value) {
    console.error('❌ AuthContext value is undefined');
    return null;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para usar o contexto de autenticação
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

